import { GOOGLE_PIN_FIELD_ID } from "@shared/azure/googleServiceAccountPin";
import type { ConnectorDefinition, FormValues } from "./types";

/**
 * #1932 (owner 2026-10-30, D3): in an environment that pins a Google service account, the GA4 and
 * Search Console detail carries it as `pinnedServiceAccount` for a person who edits connections.
 * The editor fills the field with it and shows it read-only, so nobody types an address the import
 * would refuse. A Viewer is never sent it, and reads the field Hidden (#1107).
 */

/** The words under a pinned field. */
export const PINNED_FIELD_HINT = "Skypoint sets this for your organisation. It cannot be changed.";

/** Added when the connection stores another address than the pinned one (review of #2429). */
export const PINNED_FIELD_CHANGED_HINT =
  "Skypoint changed this address. Save the connection to use it, then validate it.";

/**
 * The hint under a pinned field: the pinned sentence, the changed sentence when the stored value
 * differs, then the seed's own help (which says where to add the address in Google), so the next
 * step stays on screen.
 */
export function pinnedFieldHint(helpText: string | undefined, changed: boolean): string {
  return [PINNED_FIELD_HINT, changed ? PINNED_FIELD_CHANGED_HINT : null, helpText?.trim() || null]
    .filter((part): part is string => part !== null)
    .join(" ");
}

/** Field id to the value the environment pins, for the fields this definition has. */
export function pinnedFieldsOf(
  definition: Pick<ConnectorDefinition, "fieldDefinitions" | "pinnedServiceAccount"> | undefined,
): ReadonlyMap<string, string> {
  const pin = definition?.pinnedServiceAccount;
  if (!pin) return NONE;
  const hasField = definition.fieldDefinitions.some((field) => field.id === GOOGLE_PIN_FIELD_ID);
  return hasField ? new Map([[GOOGLE_PIN_FIELD_ID, pin]]) : NONE;
}

/** `values` with every pinned field set to its pinned value. */
export function withPinnedValues(
  values: FormValues,
  pinned: ReadonlyMap<string, string>,
): FormValues {
  if (pinned.size === 0) return values;
  return { ...values, ...Object.fromEntries(pinned) };
}

/** The pinned fields whose stored value is not the pinned one. */
export function pinnedFieldsChanged(
  stored: FormValues,
  pinned: ReadonlyMap<string, string>,
): ReadonlySet<string> {
  return new Set([...pinned].filter(([id, value]) => stored[id] !== value).map(([id]) => id));
}

/** Whether `values` already hold every pinned value. */
export function holdsPinnedValues(
  values: FormValues,
  pinned: ReadonlyMap<string, string>,
): boolean {
  for (const [id, value] of pinned) if (values[id] !== value) return false;
  return true;
}

const NONE: ReadonlyMap<string, string> = new Map();
